import React, { useMemo, useState } from 'react';
import { ChevronLeft, Plus } from 'lucide-react';
import { flashRowProps, Progress, Table } from '../components/ui.jsx';
import { ClientModal } from '../components/AdminModals.jsx';
import { ListToolbar, PageHeader } from '../components/PageSkeleton.jsx';
import { useClientDetail } from './client/useClientDetail.js';
import { sanitizeShape, sortOf } from '../lib/sessionState.js';
import { useSessionState } from '../lib/useSessionState.js';
import { DETAIL_TABS } from '../lib/urlState.js';
import { countActiveFilters } from '../lib/listFilters.js';
import { Select } from '../components/controls/Select.jsx';
import { ResetFiltersButton } from '../components/controls/ResetFiltersButton.jsx';
import { ClientInfoTab } from './client/ClientInfoTab.jsx';
import { ClientEffortTab } from './client/ClientEffortTab.jsx';
import { ClientOutlookTab } from './client/ClientOutlookTab.jsx';
import { ClientBillingTab } from './client/ClientBillingTab.jsx';
import { ClientTransactionTab } from './client/ClientTransactionTab.jsx';

// Labels for the tabs in `DETAIL_TABS.clients` (which also sets their order).
const CLIENT_TAB_LABELS = {
  info: 'Client Info',
  effort: 'Effort Tracker',
  outlook: 'Outlook',
  transactions: 'Transaction data',
  billing: 'Billing',
};

const CLIENT_FILTERS = { search: '', tier: 'all', group: 'all' };
const CLIENT_SORT = { key: 'name', direction: 'asc' };
const ALL_ONLY = ['all'];

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const CLIENT_TABLE_HEADERS = [
  { label: 'Client name', sortKey: 'name' },
  { label: 'Tier', sortKey: 'tier' },
  { label: 'Group', sortKey: 'group' },
  { label: 'Projects', sortKey: 'projects' },
  { label: 'Budget', sortKey: 'budget' },
  { label: 'Spent', sortKey: 'spent' },
  { label: 'Remaining', sortKey: 'remaining' },
  { label: 'Budget burn', sortKey: 'burn' },
];
const SORT_SHAPE = sortOf(CLIENT_TABLE_HEADERS.map((header) => header.sortKey));

// Distinct, non-empty values in display order — used to populate the Tier
// and Group filter dropdowns from whatever the data actually contains,
// rather than a hard-coded list that would drift from real values.
const distinctValues = (rows, key) =>
  [...new Set(rows.map((row) => row[key]).filter(Boolean))].sort((a, b) =>
    collator.compare(a, b),
  );

function clientRows(data) {
  return data.clients.map((client) => {
    const projects = data.allProjects.filter(
      (project) => project.clientId === client.id,
    );
    const budgetedProjects = projects.filter(
      (project) => project.status === 'active' && project.budgetMD > 0,
    );
    const budget = budgetedProjects.reduce(
      (sum, project) => sum + project.budgetMD,
      0,
    );
    const budgetedSpent = budgetedProjects.reduce(
      (sum, project) => sum + project.spentMD,
      0,
    );
    const remaining = budget - budgetedSpent;
    return {
      ...client,
      projects,
      budgetedProjects,
      budget,
      budgetedSpent,
      remaining,
    };
  });
}

export function Clients({ data, reload, onOpen }) {
  const [show, setShow] = useState(false);
  const [filters, setFilters] = useSessionState(
    'clients.filters',
    CLIENT_FILTERS,
    sanitizeShape,
  );
  const [sort, setSort] = useSessionState(
    'clients.sort',
    CLIENT_SORT,
    SORT_SHAPE,
  );
  const [flashToken, setFlashToken] = useState(0);
  const setFilter = (key) => (value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const rows = useMemo(() => clientRows(data), [data]);
  const tierOptions = useMemo(() => distinctValues(rows, 'tier'), [rows]);
  const groupOptions = useMemo(() => distinctValues(rows, 'groupName'), [rows]);
  // A remembered tier or group no client has any more falls back to "All".
  const tierFilter = tierOptions.includes(filters.tier) ? filters.tier : 'all';
  const groupFilter = groupOptions.includes(filters.group)
    ? filters.group
    : 'all';
  const search = filters.search;
  const activeCount = countActiveFilters(
    { search, tier: tierFilter, group: groupFilter },
    CLIENT_FILTERS,
  );
  // The list runs entirely off the bootstrap payload — no report query.
  // Anything time-windowed (hours/MDs in a period) lives in Reports; this view
  // only answers "which clients exist and how their project budgets stand"
  // with all-time figures.
  const visibleRows = rows
    .filter((client) =>
      client.name.toLowerCase().includes(search.toLowerCase()),
    )
    .filter((client) => tierFilter === 'all' || client.tier === tierFilter)
    .filter(
      (client) => groupFilter === 'all' || client.groupName === groupFilter,
    )
    .sort((a, b) => {
      const left = clientSortValue(a, sort.key);
      const right = clientSortValue(b, sort.key);
      const result =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : collator.compare(String(left), String(right));
      return sort.direction === 'asc' ? result : -result;
    });

  function sortBy(key) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
    setFlashToken((n) => n + 1);
  }

  function renderClientRow(client) {
    const hasBudget = client.budget > 0;
    // Raw percentage — the Progress bar clamps its own width, but `warn` /
    // `danger` need the true value so an over-budget client reads as danger.
    const pct = hasBudget ? (client.budgetedSpent / client.budget) * 100 : 0;
    const { key, className } = flashRowProps(
      client.id,
      flashToken,
      'clickable',
    );
    return (
      <tr key={key} className={className} onClick={() => onOpen(client.id)}>
        <td className="strong">{client.name}</td>
        <td>{client.tier}</td>
        <td>{client.groupName || <span className="muted-text">—</span>}</td>
        <td>{client.projects.length}</td>
        <td className="strong">
          {hasBudget ? (
            `${client.budget.toFixed(1)} MD`
          ) : (
            <span className="muted-text">No budget</span>
          )}
        </td>
        <td className="strong">{client.budgetedSpent.toFixed(1)} MD</td>
        <td className="strong">
          {hasBudget ? (
            `${client.remaining.toFixed(1)} MD`
          ) : (
            <span className="muted-text">N/A</span>
          )}
        </td>
        <td>
          {hasBudget ? (
            <Progress pct={pct} warn={pct > 80} danger={pct > 100} />
          ) : (
            <span className="muted-text">No budget</span>
          )}
        </td>
      </tr>
    );
  }

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle={`${data.clients.length} active clients · ${rows.reduce((sum, client) => sum + client.projects.length, 0)} projects`}
        actions={
          <button className="primary" onClick={() => setShow(true)}>
            <Plus size={14} /> New client
          </button>
        }
      />
      <ListToolbar
        search={search}
        onSearch={setFilter('search')}
        placeholder="Search clients"
        filters={
          <div className="toolbar-filters">
            <Select
              value={tierFilter}
              onChange={setFilter('tier')}
              ariaLabel="Filter by tier"
              active={tierFilter !== 'all'}
              options={[
                { value: 'all', label: 'All tiers' },
                ...tierOptions.map((tier) => ({ value: tier, label: tier })),
              ]}
            />
            <Select
              value={groupFilter}
              onChange={setFilter('group')}
              ariaLabel="Filter by group"
              active={groupFilter !== 'all'}
              keepOnSearch={ALL_ONLY}
              options={[
                { value: 'all', label: 'All groups' },
                ...groupOptions.map((group) => ({
                  value: group,
                  label: group,
                })),
              ]}
            />
            <ResetFiltersButton
              count={activeCount}
              onReset={() => setFilters(CLIENT_FILTERS)}
            />
          </div>
        }
      />
      <Table headers={CLIENT_TABLE_HEADERS} sort={sort} onSort={sortBy}>
        {visibleRows.map(renderClientRow)}
      </Table>
      {show && (
        <ClientModal
          onClose={() => setShow(false)}
          onCreated={async () => {
            setShow(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function clientSortValue(client, key) {
  if (key === 'name') return client.name;
  if (key === 'tier') return client.tier || '';
  if (key === 'group') return client.groupName || '';
  if (key === 'projects') return client.projects.length;
  if (key === 'budget') return client.budget;
  if (key === 'spent') return client.budgetedSpent;
  if (key === 'remaining')
    return client.budget > 0 ? client.remaining : Number.NEGATIVE_INFINITY;
  if (key === 'burn')
    return client.budget > 0 ? client.budgetedSpent / client.budget : -1;
  return '';
}

export function ClientDetail({
  clientId,
  data,
  reload,
  tab: urlTab,
  onTabChange: setTab,
  onBack,
  onOpenProject,
}) {
  const client = clientRows(data).find((item) => item.id === clientId);
  const tab = urlTab || DETAIL_TABS.clients[0];
  const {
    detail,
    loading: detailLoading,
    loaded: detailLoaded,
    error: detailError,
    reload: reloadDetail,
  } = useClientDetail(clientId);

  // Which profile group (if any) is mid-edit/save. Lives here, not inside
  // ClientInfoTab: that tab unmounts whenever the user switches to another
  // tab and back, and a component-local lock would forget an in-flight save
  // the moment the user switches away and back, letting a second group
  // submit its own stale snapshot and silently revert the first.
  const [editingProfileGroup, setEditingProfileGroup] = useState(null);

  return (
    <div>
      <button className="secondary detail-back" onClick={onBack}>
        <ChevronLeft size={14} /> All clients
      </button>
      <div className="detail-head">
        <span className="code big">
          {client.name.slice(0, 2).toUpperCase()}
        </span>
        <div>
          <h1>{client.name}</h1>
          <p>
            {client.tier}
            {client.groupName ? ` · ${client.groupName}` : ''} ·{' '}
            {client.projects.length} project
            {client.projects.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <nav className="tabs project-detail-tabs">
        {DETAIL_TABS.clients.map((key) => (
          <button
            key={key}
            className={tab === key ? 'active' : ''}
            onClick={() => setTab(key)}
          >
            {CLIENT_TAB_LABELS[key]}
          </button>
        ))}
      </nav>

      {tab === 'effort' && (
        <ClientEffortTab
          client={client}
          data={data}
          onOpenProject={onOpenProject}
        />
      )}
      {tab === 'transactions' && (
        <ClientTransactionTab
          client={client}
          data={data}
          onOpenInfo={() => setTab('info')}
        />
      )}
      {tab === 'billing' && <ClientBillingTab client={client} />}

      {(tab === 'info' || tab === 'outlook') &&
        // A failed refresh after the first load leaves `detail` stale — drop
        // to the retry fallback rather than keep the editors live against it.
        (detailLoaded && !detailError ? (
          <>
            {tab === 'info' && (
              <ClientInfoTab
                client={client}
                data={data}
                detail={detail}
                reload={reload}
                reloadDetail={reloadDetail}
                onOpenProject={onOpenProject}
                editingGroup={editingProfileGroup}
                setEditingGroup={setEditingProfileGroup}
              />
            )}
            {tab === 'outlook' && (
              <ClientOutlookTab detail={detail} loading={detailLoading} />
            )}
          </>
        ) : (
          <DetailFallback
            error={detailError}
            onRetry={() => reloadDetail().catch(() => {})}
          />
        ))}
    </div>
  );
}

function DetailFallback({ error, onRetry }) {
  return (
    <div className="empty-panel">
      {error ? (
        <>
          Couldn’t load the details for this client.{' '}
          <button type="button" className="secondary small" onClick={onRetry}>
            Retry
          </button>
        </>
      ) : (
        'Loading…'
      )}
    </div>
  );
}
